import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import React, {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {STPUIEntity} from '../../../../store/control/state';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useControlSelector} from '../../../../hooks/control-selector';
import {useLocalization} from "../../../../hooks/localization";
import {TFunction} from "i18next";

const BACK_URL = '/settings/stp-editor';

const getColumns = (bridgeDeviceTypeId: string, t:TFunction): ColumnModel[] => [
    {
        length: 3,
        name: t('display_name'),
        key: 'display_name',
    },
    {
        length: 3,
        name: t('type'),
        key: 'type',
    },
    ...(bridgeDeviceTypeId === '23' ? [] :
        [{
            length: 1,
            name: t('page'),
            key: 'page_number',
        }]),
    {
        length: 1,
        name: bridgeDeviceTypeId === '23' ? t('button') : t('row'),
        key: 'row_number',
    },
];

const keyExtractor = (item: STPUIEntity) => item.id;

const rowNumberToString = (n: number) => {
    if (typeof n !== 'number' || n <= 0) {
        return '-';
    }

    let str = '';
    for (let i = 1; i < n; i++) {
        str += i.toString() + ',';
    }
    str += n.toString();
    return str;
};

export function STPEditorContentListScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();

    const devices = useSelector((state) => state.control.bridgeDeviceList);
    const deviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const selectedDevice = useMemo(() => devices.find((d) => d.id === params.id), [devices, params.id]);
    const selectedDeviceType = useMemo(() => deviceTypeSet[selectedDevice.type_id], [deviceTypeSet, selectedDevice]);
    const stpUIEntityList = useFilterSelector(state => state.control.stpUIList,
        (e) => e.bridge_device_id === selectedDevice.id);
    const sortedEntityList = useMemo(() => stpUIEntityList.sort((a, b) => (a.page_number * 10 + a.row_number) - (b.page_number * 10 + b.row_number)), [stpUIEntityList]);

    const [selectedUiEntity, setSelectedUiEntity] = useState<STPUIEntity>();
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const controlList = useControlSelector();

    const buttons = useMemo(() => [
        {
            text: t('edit'),
            url: `/settings/stp-editor/${selectedDevice?.id}/content/${selectedUiEntity?.id}`,
            className: 'blue',
            hidden: !selectedUiEntity,
        },
        {
            text: t('add'),
            url: `/settings/stp-editor/${selectedDevice?.id}/content`,
            className: 'green',
        },
    ], [t, selectedDevice.id, selectedUiEntity]);

    const displayNameExtractor = useCallback((item: STPUIEntity, column: ColumnModel) => {
        if (column.key === 'type') {
            switch (item.type) {
                case 'group':
                    return t('group_control');
                case 'weather':
                    return t('weather_state_time');
                case 'scenario':
                    return t('scenario');
                case 'media':
                    return t('media');
                case 'air':
                    return t('air_conditioning');
                case 'control':
                case 'virtualControl':
                    const control = controlList.find((c) => c.id === item.relational_id);
                    return controlTypeSet[control?.control.type_id]?.name || t('control');
                default:
                    return '-';
            }
        }

        if (column.key === 'display_name') {
            return item.display_name || '-';
        }

        if (column.key === 'page_number') {
            return (item.page_number === 0 && selectedDeviceType.has_home_page) ? t('home_page') : item.page_number ?? '-';
        }

        if (column.key === 'row_number') {
            return (item.type === 'air' || item.type === 'media') ?
                rowNumberToString(selectedDeviceType.row_count) :
                item.row_number || '-';
        }

        return item[column.key] || '-';
    }, [t, controlList, controlTypeSet, selectedDeviceType.has_home_page, selectedDeviceType?.row_count]);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                searchable={true}>
                <SettingsList
                    columns={getColumns(selectedDeviceType.id, t)}
                    items={sortedEntityList}
                    selectedItem={selectedUiEntity}
                    onItemSelected={setSelectedUiEntity}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
}
